import {
  CmrArticle2OtherModeType,
  CmrOptionalParticularsApplicable,
  ECMR,
  LogisticsLocation,
  LogisticsPackage,
  SupplementaryDocumentEvidenceReference,
  SupplyChainConsignmentItem,
  SupplyChainConsignment,
  TradeAddress,
  TradeParty,
  TransportEvent,
} from '../types/eCMR';
import {
  CmrRequirement,
  CmrRequirementSource,
  mandatoryCmrRequirements,
  selectedOptionalCmrRequirements,
} from './cmrRequirements';
import { iso4217ChargeCurrencyCodes } from './iso4217ChargeCurrencyCodes';

export interface CmrValidationError {
  requirementId: string;
  severity: CmrRequirement['severity'];
  message: string;
  fieldPath: string;
  source: CmrRequirementSource;
}

export interface CmrValidationResult {
  valid: boolean;
  errors: CmrValidationError[];
}

type ExtendedConsignment = SupplyChainConsignment & {
  carriageCharges?: unknown;
  charges?: unknown;
  customsInstructions?: unknown;
};

type OptionalApplicabilityKey = keyof CmrOptionalParticularsApplicable;

const cmrArticle2OtherModeTypes = new Set<CmrArticle2OtherModeType>([
  'sea',
  'rail',
  'inland_waterways',
  'air',
]);

type AmountLike = {
  value?: unknown;
  amount?: unknown;
  currencyID?: unknown;
  currency?: unknown;
};

const cmrContractingCountryCodes = new Set([
  // UN Treaty Collection XI-B-11, status as at 2026-07-11: 58 parties.
  'AF', 'AL', 'AM', 'AT', 'AZ', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ',
  'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GB', 'GE', 'GR', 'HR', 'HU', 'IE',
  'IR', 'IT', 'JO', 'KG', 'KZ', 'LB', 'LT', 'LU', 'LV', 'MA', 'MD', 'ME',
  'MK', 'MN', 'MT', 'NL', 'NO', 'OM', 'PK', 'PL', 'PT', 'RO', 'RS', 'RU',
  'SE', 'SI', 'SK', 'SY', 'TJ', 'TM', 'TN', 'TR', 'UA', 'UZ',
]);

const ecmrProtocolPartyCountryCodes = new Set([
  // UN Treaty Collection XI-B-11-b, status as at 2026-07-09: 41 parties.
  'AL', 'AM', 'AT', 'AZ', 'BG', 'BY', 'CH', 'CZ', 'DE', 'DK', 'EE', 'ES',
  'FI', 'FR', 'GB', 'GR', 'HU', 'IR', 'IT', 'KG', 'LT', 'LU', 'LV', 'MD',
  'ME', 'MK', 'NL', 'NO', 'OM', 'PL', 'PT', 'RO', 'RU', 'SE', 'SI', 'SK',
  'TJ', 'TM', 'TR', 'UA', 'UZ',
]);
const ecmrRoadTransportModeCodes = new Set(['3']);

export const treatySnapshotMetadata = Object.freeze({
  cmrConvention: Object.freeze({
    source: 'UN Treaty Collection XI-B-11',
    statusDate: '2026-07-11',
    partyCount: cmrContractingCountryCodes.size,
  }),
  ecmrProtocol: Object.freeze({
    source: 'UN Treaty Collection XI-B-11-b',
    statusDate: '2026-07-09',
    partyCount: ecmrProtocolPartyCountryCodes.size,
  }),
});

const hasText = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (Array.isArray(value)) {
    return value.some(hasText);
  }

  if (value && typeof value === 'object') {
    return Object.values(value).some(hasText);
  }

  return false;
};

const hasPositiveNumberLike = (value: unknown): boolean => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0;
  }

  if (typeof value === 'string') {
    const numeric = Number(value);
    return value.trim().length > 0 && Number.isFinite(numeric) && numeric > 0;
  }

  return false;
};

const calendarDateTimeIsValid = (
  year: string,
  month: string,
  day: string,
  hour = '0',
  minute = '0',
  second = '0'
): boolean => {
  const values = [year, month, day, hour, minute, second].map((value) => Number(value));
  const [yearNumber, monthNumber, dayNumber, hourNumber, minuteNumber, secondNumber] = values;

  if (
    !values.every(Number.isInteger) ||
    monthNumber < 1 ||
    monthNumber > 12 ||
    hourNumber < 0 ||
    hourNumber > 23 ||
    minuteNumber < 0 ||
    minuteNumber > 59 ||
    secondNumber < 0 ||
    secondNumber > 59
  ) {
    return false;
  }

  const parsed = new Date(Date.UTC(yearNumber, monthNumber - 1, dayNumber, hourNumber, minuteNumber, secondNumber));
  return (
    parsed.getUTCFullYear() === yearNumber &&
    parsed.getUTCMonth() === monthNumber - 1 &&
    parsed.getUTCDate() === dayNumber &&
    parsed.getUTCHours() === hourNumber &&
    parsed.getUTCMinutes() === minuteNumber &&
    parsed.getUTCSeconds() === secondNumber
  );
};

const hasValidDateTimeString = (value: unknown): boolean => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return false;
  }

  const text = String(value).trim();
  const compactMatch = text.match(/^(\d{4})(\d{2})(\d{2})(?:(\d{2})(\d{2})(\d{2})?)?$/);
  const isoMatch = text.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/
  );
  const match = compactMatch || isoMatch;

  if (!match) {
    return false;
  }

  const [, year, month, day, hour, minute, second] = match;
  return calendarDateTimeIsValid(year, month, day, hour, minute, second);
};

const amountLikeHasPositiveValue = (amount: unknown): boolean => {
  if (hasPositiveNumberLike(amount)) {
    return true;
  }

  if (amount && typeof amount === 'object') {
    const candidate = amount as AmountLike;
    return hasPositiveNumberLike(candidate.value) || hasPositiveNumberLike(candidate.amount);
  }

  return false;
};

const amountLikeHasCurrency = (amount: unknown): boolean => {
  if (!amount || typeof amount !== 'object') {
    return false;
  }

  const candidate = amount as AmountLike;
  return [candidate.currencyID, candidate.currency].some((value) => {
    if (!hasText(value)) {
      return false;
    }

    const normalized = String(value).trim().toUpperCase();
    return normalized.length === 3 && iso4217ChargeCurrencyCodes.has(normalized);
  });
};

const amountLikeHasPositiveCurrencyValue = (
  amount: unknown,
  currencySource: unknown = amount
): boolean =>
  amountLikeHasPositiveValue(amount) &&
  (amountLikeHasCurrency(amount) || amountLikeHasCurrency(currencySource));

const measureHasPositiveValue = (measure: unknown): boolean => {
  if (!measure || typeof measure !== 'object') {
    return false;
  }

  const candidate = measure as { value?: unknown };
  return hasPositiveNumberLike(candidate.value);
};

const partyTextFragments = (value: unknown): string[] => {
  if (typeof value === 'string' || typeof value === 'number') {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap(partyTextFragments);
  }

  return [];
};

const partyNameNegatedPattern =
  /\b(no|not|without|none|missing|unknown)\s+(sender|consignor|shipper|carrier|consignee|receiver|recipient|party|name)\b|\b(sender|consignor|shipper|carrier|consignee|receiver|recipient|party|name)\b.{0,60}\b(not|never)\s+(declared|provided|identified|specified|available|known|present)\b|\b(sin\s+(remitente|expedidor|cargador|transportista|porteador|destinatario|nombre)|remitente\s+no\s+(declarado|identificado|especificado)|transportista\s+no\s+(declarado|identificado|especificado)|destinatario\s+no\s+(declarado|identificado|especificado))\b/i;
const spanishPartyNameTermNegatedPattern =
  /\b(?:remitentes?|expedidores?|cargadores?|transportistas?|porteadores?|destinatarios?|nombres?)\s+(?:desconocid[ao]s?|no\s+(?:declarad[ao]s?|identificad[ao]s?|especificad[ao]s?|proporcionad[ao]s?|disponibles?|conocid[ao]s?|indicad[ao]s?|present[ea]s?))\b/i;

const partyAddressNegatedPattern =
  /\b(no|not|without|none|missing|unknown)\s+(address|postal\s+address|street|city|postcode|postal\s+code|domicile)\b|\b(address|postal\s+address|street|city|postcode|postal\s+code|domicile)\b.{0,60}\b(not|never)\s+(declared|provided|identified|specified|available|known|present)\b|\b(sin\s+(direccion|direcci[oó]n|domicilio|calle|ciudad|codigo\s+postal|c[oó]digo\s+postal)|direcci[oó]n\s+no\s+(declarada|identificada|especificada|proporcionada)|domicilio\s+no\s+(declarado|identificado|especificado|proporcionado))\b/i;
const spanishPartyAddressTermNegatedPattern =
  /\b(?:direccion(?:es)?|domicilios?|calles?|ciudad(?:es)?|codigos?\s+postales?)\s+(?:desconocid[ao]s?|no\s+(?:declarad[ao]s?|identificad[ao]s?|especificad[ao]s?|proporcionad[ao]s?|disponibles?|conocid[ao]s?|indicad[ao]s?|present[ea]s?))\b/i;

const hasPositivePartyNameText = (value: unknown): boolean => {
  const text = partyTextFragments(value)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return (
    hasText(text) &&
    !partyNameNegatedPattern.test(text) &&
    !spanishPartyNameTermNegatedPattern.test(text)
  );
};

const hasPositivePartyAddressText = (value: unknown): boolean => {
  const text = partyTextFragments(value)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return (
    hasText(text) &&
    !partyAddressNegatedPattern.test(text) &&
    !spanishPartyAddressTermNegatedPattern.test(text)
  );
};

const hasAddress = (address?: TradeAddress): boolean =>
  Boolean(
    address &&
      hasText(address.CountryID) &&
      (hasPositivePartyAddressText(address.StreetName) ||
        hasPositivePartyAddressText(address.CityName) ||
        hasPositivePartyAddressText(address.PostcodeCode))
  );

const hasNamedAddress = (party?: TradeParty): boolean =>
  Boolean(party && hasPositivePartyNameText(party.Name) && hasAddress(party.PostalTradeAddress));

const normalizedCountryCode = (address?: TradeAddress): string => {
  if (!hasText(address?.CountryID)) {
    return '';
  }

  const normalized = String(address?.CountryID ?? '')
    .replace(/[^A-Za-z]/g, '')
    .toUpperCase();

  return normalized.length === 2 ? normalized : '';
};

const locationTextFragments = (value: unknown): string[] => {
  if (typeof value === 'string' || typeof value === 'number') {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap(locationTextFragments);
  }

  return [];
};

const locationNegatedPattern =
  /\b(no|not|without|none|missing|unknown)\s+(place|location|address|site|pickup|delivery|issue\s+(place|location)|taking[-\s]?over\s+(place|location))\b|\b(place|location|address|site|pickup|delivery|issue\s+(place|location)|taking[-\s]?over\s+(place|location))\b.{0,60}\b(not|never)\s+(declared|provided|identified|specified|available|known|present)\b|\b(sin\s+(lugar|ubicaci[oó]n|direccion|direcci[oó]n|domicilio)|lugar\s+no\s+(declarado|identificado|especificado|proporcionado)|ubicaci[oó]n\s+no\s+(declarada|identificada|especificada|proporcionada))\b/i;
const spanishLocationTermNegatedPattern =
  /\b(?:lugar(?:es)?|ubicacion(?:es)?|direccion(?:es)?|domicilios?)\s+(?:desconocid[ao]s?|no\s+(?:declarad[ao]s?|identificad[ao]s?|especificad[ao]s?|proporcionad[ao]s?|disponibles?|conocid[ao]s?|indicad[ao]s?|present[ea]s?))\b/i;

const hasPositiveLocationText = (value: unknown): boolean => {
  const text = locationTextFragments(value)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return (
    hasText(text) &&
    !locationNegatedPattern.test(text) &&
    !spanishLocationTermNegatedPattern.test(text)
  );
};

const hasAddressLocationDetail = (address?: TradeAddress): boolean =>
  Boolean(
    address &&
      hasText(address.CountryID) &&
      (hasPositiveLocationText(address.StreetName) ||
        hasPositiveLocationText(address.CityName) ||
        hasPositiveLocationText(address.PostcodeCode))
  );

const hasLocation = (location?: LogisticsLocation): boolean =>
  Boolean(
    location &&
      (hasPositiveLocationText(location.Name) ||
        hasPositiveLocationText(location.Description) ||
        hasPositiveLocationText(location.ID) ||
        hasAddressLocationDetail(location.PostalTradeAddress))
  );

const hasEventDate = (event?: TransportEvent): boolean =>
  Boolean(
    event &&
      (hasValidDateTimeString(event.ScheduledOccurrenceDateTime?.DateTimeString) ||
        hasValidDateTimeString(event.ActualOccurrenceDateTime?.DateTimeString) ||
        hasValidDateTimeString(event.EstimatedOccurrenceDateTime?.DateTimeString))
  );

const mainTransportModeIsRoadCompatible = (consignment?: SupplyChainConsignment): boolean =>
  consignment?.MainCarriageLogisticsTransportMovement?.every(
    (movement) =>
      !hasText(movement.ModeCode) ||
      ecmrRoadTransportModeCodes.has(String(movement.ModeCode).trim())
  ) ?? true;

const hasInternationalCarriageScope = (consignment?: SupplyChainConsignment): boolean => {
  const pickupCountry = normalizedCountryCode(
    consignment?.CarrierAcceptanceLogisticsLocation?.PostalTradeAddress
  );
  const deliveryCountry = normalizedCountryCode(
    consignment?.ConsigneeReceiptLogisticsLocation?.PostalTradeAddress
  );

  if (!pickupCountry || !deliveryCountry) {
    return false;
  }

  return (
    mainTransportModeIsRoadCompatible(consignment) &&
    pickupCountry !== deliveryCountry &&
    (cmrContractingCountryCodes.has(pickupCountry) ||
      cmrContractingCountryCodes.has(deliveryCountry))
  );
};

const hasEcmrProtocolPartyScope = (consignment?: SupplyChainConsignment): boolean => {
  const pickupCountry = normalizedCountryCode(
    consignment?.CarrierAcceptanceLogisticsLocation?.PostalTradeAddress
  );
  const deliveryCountry = normalizedCountryCode(
    consignment?.ConsigneeReceiptLogisticsLocation?.PostalTradeAddress
  );

  if (!pickupCountry || !deliveryCountry) {
    return false;
  }

  return (
    ecmrProtocolPartyCountryCodes.has(pickupCountry) ||
    ecmrProtocolPartyCountryCodes.has(deliveryCountry)
  );
};

const hasPositiveItemSequence = (item: SupplyChainConsignmentItem): boolean =>
  hasPositiveNumberLike(item.SequenceNumeric);

const packages = (consignment?: SupplyChainConsignment): LogisticsPackage[] =>
  consignment?.IncludedSupplyChainConsignmentItem?.flatMap(
    (item) => (hasPositiveItemSequence(item) ? item.TransportLogisticsPackage ?? [] : [])
  ) ?? [];

const packageMarkTextFragments = (value: unknown): string[] => {
  if (typeof value === 'string' || typeof value === 'number') {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap(packageMarkTextFragments);
  }

  return [];
};

const packageMarkNegatedPattern =
  /\b(no|not|without|none|missing)\s+((shipping|package)\s+)?(marks?|markings?|numbers?|identifiers?|references?)\b|\b((shipping|package)\s+)?(marks?|markings?|numbers?|identifiers?|references?)\b.{0,60}\b(not|never)\s+(provided|available|declared|assigned|marked|numbered|present)\b|\b(sin\s+(marcas?|numeraci[oó]n|identificadores?|referencias?)|marcas?\s+no\s+(declaradas?|asignadas?|disponibles?)|sin\s+marcar|sin\s+numerar)\b/i;
const spanishPackageMarkTermNegatedPattern =
  /\b(?:marcas?|numeracion(?:es)?|numeros?|identificadores?|referencias?)\s+(?:desconocid[ao]s?|no\s+(?:declarad[ao]s?|asignad[ao]s?|disponibles?|conocid[ao]s?|indicad[ao]s?|present[ea]s?|aportad[ao]s?|marcad[ao]s?|numerad[ao]s?))\b/i;

const hasPositivePackageMarkText = (value: unknown): boolean => {
  const text = packageMarkTextFragments(value)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return (
    hasText(text) &&
    !packageMarkNegatedPattern.test(text) &&
    !spanishPackageMarkTermNegatedPattern.test(text)
  );
};

const hasPackageMark = (logisticsPackage: LogisticsPackage): boolean =>
  hasPositivePackageMarkText(logisticsPackage.ID) ||
  Boolean(
    logisticsPackage.PhysicalLogisticsShippingMarks?.some((marks) =>
      hasPositivePackageMarkText(marks.Marking)
    )
  );

const goodsNatureTextFragments = (value: unknown): string[] => {
  if (typeof value === 'string' || typeof value === 'number') {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap(goodsNatureTextFragments);
  }

  return [];
};

const goodsNatureNegatedPattern =
  /\b(no|not|without|none|missing)\s+(goods?|cargo|freight|merchandise|commodit(y|ies)|items?)\b|\b(goods?|cargo|freight|merchandise|commodit(y|ies)|items?)\b.{0,60}\b(not|never)\s+(declared|provided|identified|specified|listed|loaded|carried|present)\b|\b(sin\s+(mercanc[ií]as?|carga|bienes?)|mercanc[ií]as?\s+no\s+(declaradas?|identificadas?|especificadas?)|sin\s+carga)\b/i;
const spanishGoodsNatureTermNegatedPattern =
  /\b(?:mercancias?|carga|bienes?)\s+(?:desconocid[ao]s?|no\s+(?:declarad[ao]s?|identificad[ao]s?|especificad[ao]s?|proporcionad[ao]s?|disponibles?|conocid[ao]s?|indicad[ao]s?|present[ea]s?|aportad[ao]s?))\b/i;

const hasPositiveGoodsNatureText = (value: unknown): boolean => {
  const text = goodsNatureTextFragments(value)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return (
    hasText(text) &&
    !goodsNatureNegatedPattern.test(text) &&
    !spanishGoodsNatureTermNegatedPattern.test(text)
  );
};

const packagePackingTextFragments = (value: unknown): string[] => {
  if (typeof value === 'string' || typeof value === 'number') {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap(packagePackingTextFragments);
  }

  return [];
};

const packagePackingNegatedPattern =
  /\b(no|not|without|none|missing)\s+(packing|packaging|package\s+(type|method)|packing\s+method|packaging\s+method|container|wrapping)\b|\b(packing|packaging|package\s+(type|method)|packing\s+method|packaging\s+method|container|wrapping)\b.{0,60}\b(not|never)\s+(declared|provided|identified|specified|available|present|used)\b|\b(sin\s+(embalaje|empaque|envasado)|embalaje\s+no\s+(declarado|identificado|especificado|proporcionado)|empaque\s+no\s+(declarado|identificado|especificado|proporcionado))\b/i;
const spanishPackagePackingTermNegatedPattern =
  /\b(?:embalajes?|empaques?|envasados?)\s+(?:desconocid[ao]s?|no\s+(?:declarad[ao]s?|identificad[ao]s?|especificad[ao]s?|proporcionad[ao]s?|disponibles?|conocid[ao]s?|indicad[ao]s?|present[ea]s?|usad[ao]s?|aportad[ao]s?))\b/i;

const hasPositivePackagePackingText = (value: unknown): boolean => {
  const text = packagePackingTextFragments(value)
    .join(' ')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return (
    hasText(text) &&
    !packagePackingNegatedPattern.test(text) &&
    !spanishPackagePackingTermNegatedPattern.test(text)
  );
};

const hasRecognizedUndgIdentificationCode = (value: unknown): boolean =>
  typeof value === 'string' && /^\d{4}$/.test(value.trim());

const dangerousGoodsRegulationCodes = new Set(['ADR']);
const dangerousGoodsPackagingDangerLevelCodes = new Set(['1', '2', '3', '4']);
const packagingLevelCodes = new Set(['1', '2', '3', '4', '5', '6']);
const transportPaymentArrangementCodes = new Set(['A', 'B', 'C', 'P']);
const transportServiceCategoryCodes = new Set(['2', '12', '14', '16', '22', '23', '24']);

const optionalCodeIsRecognized = (
  value: unknown,
  recognizedCodes: ReadonlySet<string>
): boolean => !hasText(value) || recognizedCodes.has(String(value).trim().toUpperCase());

const dangerousGoodsDescriptionNegatedPattern =
  /\b(no|not|without|none)\s+(dangerous|hazardous|regulated)\s+(goods|cargo|material|substance|article)s?\b|\bnon[-\s]?(dangerous|hazardous|regulated)\s+(goods|cargo|material|substance|article)s?\b|\b(dangerous|hazardous|regulated)\s+(goods|cargo|material|substance|article)s?\b.{0,60}\b(not|never)\s+(applicable|classified|declared|regulated|restricted|required)\b|\b(not\s+classified\s+as\s+(dangerous|hazardous)|mercanc[ií]as?\s+no\s+peligrosas?|no\s+mercanc[ií]as?\s+peligrosas?)\b/i;

const spanishDangerousGoodsDescriptionUnavailablePattern =
  /\b(?:nombre\s+tecnic[ao]|denominacion\s+(?:tecnic[ao]\s+)?(?:oficial|peligrosa)?|descripcion\s+(?:tecnic[ao]\s+)?peligrosa|naturaleza\s+peligrosa|mercancias?\s+peligrosas?|carga\s+peligrosa|sustancias?\s+peligrosas?|articulos?\s+peligrosos?)\s+(?:desconocid[ao]s?|no\s+(?:disponibles?|declarad[ao]s?|identificad[ao]s?|especificad[ao]s?|proporcionad[ao]s?|conocid[ao]s?|indicad[ao]s?|present[ea]s?|aportad[ao]s?))\b/i;

const hasDangerousGoodsDescription = (...values: unknown[]): boolean => {
  const text = values
    .flat()
    .filter(hasText)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return (
    hasText(text) &&
    !dangerousGoodsDescriptionNegatedPattern.test(text) &&
    !spanishDangerousGoodsDescriptionUnavailablePattern.test(text)
  );
};

const dangerousGoodsHaveRequiredIdentificationAndDescription = (
  item: SupplyChainConsignmentItem
): boolean =>
  item.ApplicableTransportDangerousGoods?.every((dangerousGoods) =>
    hasRecognizedUndgIdentificationCode(dangerousGoods.UNDGIdentificationCode) &&
    optionalCodeIsRecognized(dangerousGoods.RegulationCode, dangerousGoodsRegulationCodes) &&
    optionalCodeIsRecognized(
      dangerousGoods.PackagingDangerLevelCode,
      dangerousGoodsPackagingDangerLevelCodes
    ) &&
    hasDangerousGoodsDescription(dangerousGoods.ProperShippingName, dangerousGoods.TechnicalName)
  ) ?? true;

const packageCodesAreRecognized = (logisticsPackage: LogisticsPackage): boolean =>
  optionalCodeIsRecognized(logisticsPackage.LevelCode, packagingLevelCodes);

const hasGoodsAndPacking = (consignment?: SupplyChainConsignment): boolean =>
  Boolean(
    consignment?.IncludedSupplyChainConsignmentItem?.some((item) => {
      const hasGoodsNature =
        hasPositiveGoodsNatureText(item.TypeCode) ||
        hasPositiveGoodsNatureText(item.NatureIdentificationTransportCargo?.Identification) ||
        hasPositiveGoodsNatureText(
          item.NatureIdentificationTransportCargo?.StatisticalClassificationCode
        );

      return (
        hasPositiveItemSequence(item) &&
        hasGoodsNature &&
        dangerousGoodsHaveRequiredIdentificationAndDescription(item) &&
        item.TransportLogisticsPackage?.some(
          (logisticsPackage) =>
            packageCodesAreRecognized(logisticsPackage) &&
            (hasPositivePackagePackingText(logisticsPackage.TypeCode) ||
              hasPositivePackagePackingText(logisticsPackage.Description) ||
              hasPositivePackagePackingText(logisticsPackage.Type))
        )
      );
    })
  );

const hasPackageNumbersAndMarks = (consignment?: SupplyChainConsignment): boolean => {
  const hasConsignmentQuantity = hasPositiveNumberLike(consignment?.ConsignmentItemQuantity);

  return packages(consignment).some(
    (logisticsPackage) =>
      packageCodesAreRecognized(logisticsPackage) &&
      (hasPositiveNumberLike(logisticsPackage.ItemQuantity) || hasConsignmentQuantity) &&
      hasPackageMark(logisticsPackage)
  );
};

const hasWeightOrQuantity = (consignment?: SupplyChainConsignment): boolean =>
  Boolean(
    hasPositiveNumberLike(consignment?.ConsignmentItemQuantity) ||
      consignment?.GrossWeightMeasure?.some(measureHasPositiveValue) ||
      consignment?.IncludedSupplyChainConsignmentItem?.some(
        (item) => hasPositiveItemSequence(item) && measureHasPositiveValue(item.GrossWeightMeasure)
      )
  );

const chargeCodesAreRecognized = (charge: unknown): boolean => {
  if (!charge || typeof charge !== 'object') {
    return true;
  }

  const candidate = charge as {
    PaymentArrangementCode?: unknown;
    CategoryCode?: unknown;
  };

  return (
    optionalCodeIsRecognized(candidate.PaymentArrangementCode, transportPaymentArrangementCodes) &&
    optionalCodeIsRecognized(candidate.CategoryCode, transportServiceCategoryCodes)
  );
};

const hasChargeAmount = (charge: unknown): boolean => {
  if (!chargeCodesAreRecognized(charge)) {
    return false;
  }

  if (amountLikeHasPositiveCurrencyValue(charge)) {
    return true;
  }

  if (charge && typeof charge === 'object') {
    const candidate = charge as {
      ActualAmount?: unknown;
      AppliedAmount?: unknown;
      amount?: unknown;
    };
    return (
      amountLikeHasPositiveCurrencyValue(candidate.amount, candidate) ||
      (Array.isArray(candidate.AppliedAmount)
        ? candidate.AppliedAmount.some((amount) => amountLikeHasPositiveCurrencyValue(amount, candidate))
        : amountLikeHasPositiveCurrencyValue(candidate.AppliedAmount, candidate)) ||
      (Array.isArray(candidate.ActualAmount)
        ? candidate.ActualAmount.some((amount) => amountLikeHasPositiveCurrencyValue(amount, candidate))
        : amountLikeHasPositiveCurrencyValue(candidate.ActualAmount, candidate))
    );
  }

  return false;
};

const hasCharges = (consignment?: ExtendedConsignment): boolean =>
  Boolean(
    (Array.isArray(consignment?.ApplicableLogisticsServiceCharge) &&
      consignment?.ApplicableLogisticsServiceCharge.some(hasChargeAmount)) ||
      (Array.isArray(consignment?.carriageCharges) && consignment?.carriageCharges.some(hasChargeAmount)) ||
      (Array.isArray(consignment?.charges) && consignment?.charges.some(hasChargeAmount)) ||
      hasChargeAmount(consignment?.carriageCharges) ||
      hasChargeAmount(consignment?.charges)
  );

const senderPaidRoleCodes = new Set(['CZ', 'CONSIGNOR', 'SENDER', 'SHIPPER']);

const isSenderPaidCharge = (charge: unknown): boolean => {
  if (!hasChargeAmount(charge) || !charge || typeof charge !== 'object') {
    return false;
  }

  const candidate = charge as {
    PayingPartyRoleCode?: unknown;
  };
  const payingPartyRoleCode = String(candidate.PayingPartyRoleCode ?? '').trim().toUpperCase();

  return senderPaidRoleCodes.has(payingPartyRoleCode);
};

const hasSenderPaidCharges = (consignment?: SupplyChainConsignment): boolean =>
  Boolean(consignment?.ApplicableLogisticsServiceCharge?.some(isSenderPaidCharge));

const customsFormalitiesCodes = new Set([
  'CUS',
  'CUSTOMS',
  'CUSTOMS_DECLARATION',
  'BORDER',
  'BORDER_CLEARANCE',
  'EXPORT',
  'EXPORT_DECLARATION',
  'IMPORT',
  'IMPORT_DECLARATION',
]);

const customsFormalitiesTextPattern =
  /customs|formalities|border|export declaration|import declaration|\bMRN\b|aduanas?|aduaneras?|formalidades\s+aduaneras?|declaracion\s+de\s+(exportacion|importacion)|frontera|tramites?\s+aduaneros?/i;
const customsFormalitiesNegatedTextPattern =
  /\b(no|not|without|none|never)\s+(customs|formalities|border|customs\s+declaration|customs\s+documents?|formalities\s+documents?|export\s+declaration|import\s+declaration|MRN)\b/i;
const customsFormalitiesTermNegatedTextPattern =
  /\b(customs|formalities|border|customs\s+declaration|customs\s+documents?|formalities\s+documents?|export\s+declaration|import\s+declaration|MRN)\b.{0,60}\b(not|never)\s+(required|needed|applicable|provided|available|submitted|included|declared)\b/i;
const spanishCustomsFormalitiesNegatedTextPattern =
  /\b(sin\s+(aduanas|formalidades|documentos?\s+aduaneros?|declaraci[oó]n\s+de\s+(exportaci[oó]n|importaci[oó]n)|MRN)|no\s+(hay|requiere|aplica|se\s+entrega|se\s+incluye|se\s+aporta).{0,60}\b(aduanas|formalidades|documentos?\s+aduaneros?|declaraci[oó]n|MRN))\b/i;

const spanishCustomsFormalitiesTermNegatedTextPattern =
  /\b(aduanas?|formalidades(?:\s+aduaneras?)?|documentos?\s+aduaneros?|declaracion\s+de\s+(exportacion|importacion)|MRN)\b.{0,60}\b(?:desconocid[ao]s?|no\s+(?:requerid[ao]s?|necesari[ao]s?|aplican?|aplicable|aportad[ao]s?|incluid[ao]s?|entregad[ao]s?|disponibles?|presentad[ao]s?|declarad[ao]s?|conocid[ao]s?|indicad[ao]s?))\b/i;

const normalizedCustomsFormalitiesCode = (value: unknown): string =>
  hasText(value)
    ? String(value)
        .replace(/[^A-Za-z0-9_]/g, '')
        .toUpperCase()
    : '';

const textMatchesCustomsFormalities = (value: unknown): boolean => {
  if (!hasText(value)) {
    return false;
  }
  const text = String(value)
    .replace(/\s+/g, ' ')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (
    customsFormalitiesNegatedTextPattern.test(text) ||
    customsFormalitiesTermNegatedTextPattern.test(text) ||
    spanishCustomsFormalitiesNegatedTextPattern.test(text) ||
    spanishCustomsFormalitiesTermNegatedTextPattern.test(text)
  ) {
    return false;
  }
  return customsFormalitiesTextPattern.test(text);
};

const isCustomsReferencedDocument = (document: unknown): boolean => {
  if (!document || typeof document !== 'object') {
    return false;
  }

  const candidate = document as { TypeCode?: unknown; ID?: unknown; Remarks?: unknown };
  if (customsFormalitiesCodes.has(normalizedCustomsFormalitiesCode(candidate.TypeCode))) {
    return true;
  }

  const referenceText = [candidate.TypeCode, candidate.ID, candidate.Remarks]
    .flat()
    .filter(Boolean)
    .join(' ');

  return textMatchesCustomsFormalities(referenceText);
};

const isCustomsInstruction = (instruction: unknown): boolean => {
  if (!instruction || typeof instruction !== 'object') {
    return false;
  }

  const candidate = instruction as { Description?: unknown; DescriptionCode?: unknown; ID?: unknown };
  return (
    customsFormalitiesCodes.has(normalizedCustomsFormalitiesCode(candidate.DescriptionCode)) ||
    textMatchesCustomsFormalities([candidate.Description, candidate.ID].flat().filter(Boolean).join(' '))
  );
};

const hasCustomsInstructions = (consignment?: ExtendedConsignment): boolean =>
  textMatchesCustomsFormalities(consignment?.customsInstructions) ||
  Boolean(
    consignment?.ConsignorProvidedBorderClearanceTransportInstructions?.some((instruction) =>
      isCustomsInstruction(instruction)
    ) || consignment?.AssociatedReferencedDocument?.some(isCustomsReferencedDocument) ||
      consignment?.PreviousAdministrativeReferencedDocument?.some(isCustomsReferencedDocument)
  );

const electronicSupplementaryDocumentTextPattern =
  /\belectronic\s+supplementary\s+document\b|\blinked\s+to\s+this\s+ecmr\b/i;
const supplementaryDocumentReferenceIdPrefix = 'OECMR-SUPPLEMENTARY-DOCUMENT-';
const supplementaryDocumentLinkReferencePattern = /^[a-f0-9]{32}$/;

const isElectronicSupplementaryReferencedDocument = (document: unknown): boolean => {
  if (!document || typeof document !== 'object') {
    return false;
  }

  const candidate = document as {
    ID?: unknown;
    URIID?: unknown;
    Remarks?: unknown;
    AttachedSpecifiedBinaryFile?: unknown;
  };
  const id = typeof candidate.ID === 'string' ? candidate.ID : '';
  if (id.startsWith(supplementaryDocumentReferenceIdPrefix)) {
    return true;
  }
  if (candidate.URIID !== undefined && candidate.URIID !== null) {
    return true;
  }

  const attachedFiles = Array.isArray(candidate.AttachedSpecifiedBinaryFile)
    ? candidate.AttachedSpecifiedBinaryFile
    : candidate.AttachedSpecifiedBinaryFile &&
      typeof candidate.AttachedSpecifiedBinaryFile === 'object'
    ? [candidate.AttachedSpecifiedBinaryFile]
    : [];
  if (attachedFiles.length) {
    return true;
  }

  const remarksText = [candidate.Remarks].flat().filter(Boolean).join(' ');
  return electronicSupplementaryDocumentTextPattern.test(remarksText);
};

const electronicSupplementaryDocumentReferences = (consignment?: SupplyChainConsignment): unknown[] => [
  ...(consignment?.AssociatedReferencedDocument ?? []),
  ...(consignment?.PreviousAdministrativeReferencedDocument ?? []),
  ...(consignment?.IncludedSupplyChainConsignmentItem ?? []).flatMap(
    (item) => item.AssociatedReferencedDocument ?? []
  ),
  ...(consignment?.IncludedSupplyChainConsignmentItem ?? []).flatMap(
    (item) => item.PreviousAdministrativeReferencedDocument ?? []
  ),
].filter(isElectronicSupplementaryReferencedDocument);

const supplementaryDocumentLinkReferenceFromXml = (document: unknown): string | undefined => {
  if (!document || typeof document !== 'object') {
    return undefined;
  }
  const id = (document as { ID?: unknown }).ID;
  if (typeof id !== 'string' || !id.startsWith(supplementaryDocumentReferenceIdPrefix)) {
    return undefined;
  }
  const reference = id.slice(supplementaryDocumentReferenceIdPrefix.length);
  return supplementaryDocumentLinkReferencePattern.test(reference) ? reference : undefined;
};

const hasCurrentVersionSupplementaryIntegrityLink = (
  reference: SupplementaryDocumentEvidenceReference
): boolean => {
  const digest = typeof reference.sha256Digest === 'string' ? reference.sha256Digest.trim() : '';
  const digestAlgorithm = reference.digestAlgorithm;
  const rawIntegrityLink = (
    hasText(reference.attachmentId) &&
    /^[a-fA-F0-9]{64}$/.test(digest) &&
    (digestAlgorithm === undefined || String(digestAlgorithm).toUpperCase() === 'SHA-256') &&
    hasText(reference.storageKey) &&
    (hasText(reference.documentVersionId) || typeof reference.documentVersionNumber === 'number')
  );
  const sanitizedIntegrityLink = (
    reference.digestAvailable === true &&
    reference.payloadStoredExternally === true &&
    (reference.documentVersionReferenceAvailable === true ||
      reference.documentVersionReferencePresent === true ||
      typeof reference.documentVersionNumber === 'number')
  );
  return Boolean(
    supplementaryDocumentLinkReferencePattern.test(reference.ecmrLinkReference ?? '') &&
    (rawIntegrityLink || sanitizedIntegrityLink)
  );
};

const hasLinkedSupplementaryDocumentEvidence = (document: ECMR): boolean => {
  const references = electronicSupplementaryDocumentReferences(document.SpecifiedSupplyChainConsignment);
  if (!references.length) {
    return true;
  }
  const availableReferences = new Set(
    (document.supplementaryDocuments ?? [])
      .filter(hasCurrentVersionSupplementaryIntegrityLink)
      .map((reference) => reference.ecmrLinkReference)
      .filter((reference): reference is string => Boolean(reference))
  );
  const usedReferences = new Set<string>();
  return references.every((reference) => {
    const linkReference = supplementaryDocumentLinkReferenceFromXml(reference);
    if (!linkReference || usedReferences.has(linkReference) || !availableReferences.has(linkReference)) {
      return false;
    }
    usedReferences.add(linkReference);
    return true;
  });
};

const textIsAffirmativeCmrStatement = (text: string): boolean => {
  const normalized = text
    .replace(/\s+/g, ' ')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\bC\s*\.?\s*M\s*\.?\s*R\b\.?/gi, 'CMR ');

  if (
    !/\bCMR\b/i.test(normalized) ||
    !/\b(Convention|provisions|Convenio|Convencion|disposiciones)\b/i.test(normalized)
  ) {
    return false;
  }

  if (
    /\b(not|never)\s+(be\s+)?(subject|governed|covered|applicable)\b/i.test(normalized) ||
    /\bnot\s+governed\b/i.test(normalized) ||
    /\bnot\s+covered\b/i.test(normalized) ||
    /\bsubject\s+to\s+no\b/i.test(normalized) ||
    /\b(CMR|Convention|provisions|Convenio|Convencion|disposiciones)\b.{0,180}\b(do\s+not|does\s+not|is\s+not|are\s+not|not|never)\s+(apply|applicable|governed|covered)\b/i.test(
      normalized
    ) ||
    /\b(CMR|Convenio|Convencion|disposiciones)\b.{0,180}\bno\s+(es|son|esta|estan|queda|quedan|resulta|resultan)?\s*(aplicable|aplicables|vigente|vigentes)\b/i.test(
      normalized
    ) ||
    /\bno\s+(queda\s+|esta\s+|estan\s+)?sujet[oa]s?\b/i.test(normalized) ||
    /\b(no\s+(queda\s+|esta\s+|está\s+)?sujet[oa]s?|no\s+se\s+(rige|aplica)|sin\s+aplicaci[oó]n)\b/i.test(
      normalized
    ) ||
    /\b(CMR|Convenio|Convencion|disposiciones)\b.{0,180}\bno\s+(se\s+)?(aplica|aplican|rige|rigen)\b/i.test(
      normalized
    )
  ) {
    return false;
  }

  return (
    /\bsubject\b.{0,180}\b(CMR|Convention|provisions)\b/i.test(normalized) ||
    /\b(CMR|Convention|provisions)\b.{0,180}\bsubject\b/i.test(normalized) ||
    /\bsujet[oa]s?\b.{0,180}\b(CMR|Convenio|Convencion|disposiciones)\b/i.test(normalized) ||
    /\b(CMR|Convenio|Convencion|disposiciones)\b.{0,180}\bsujet[oa]s?\b/i.test(normalized)
  );
};

const hasCmrStatement = (document: ECMR): boolean =>
  Boolean(
    document.ExchangedDocument?.IncludedNote?.some((note) =>
      textIsAffirmativeCmrStatement(
        [note.Subject, note.SubjectCode, note.Content, note.ContentCode]
          .flat()
          .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
          .join(' ')
      )
    )
  );

const hasDeclaredValueOrSpecialInterest = (consignment?: SupplyChainConsignment): boolean =>
  Boolean(
    consignment?.AssociatedInvoiceAmount?.some(amountLikeHasPositiveCurrencyValue) ||
      consignment?.IncludedSupplyChainConsignmentItem?.some((item) =>
        item.InvoiceAmount?.some(amountLikeHasPositiveCurrencyValue)
      )
  );

const textFragments = (value: unknown): string[] => {
  if (typeof value === 'string' || typeof value === 'number') {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap(textFragments);
  }

  return [];
};

const normalizeTextForCmrKeywordMatching = (text: string): string =>
  text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const textMatchesAgreedTimeLimit = (...values: unknown[]): boolean => {
  const text = normalizeTextForCmrKeywordMatching(
    values.flatMap(textFragments).join(' ').replace(/\s+/g, ' ').trim()
  );
  if (
    /\b(no|not|without|none)\s+(agreed\s+)?(delivery\s+)?(time[\s_-]*limits?|deadlines?|plazos?|fechas?\s+limites?|horas?\s+limites?)\b/i.test(text) ||
    /\b(time[\s_-]*limits?|delivery[\s_-]*deadlines?|deadlines?|plazos?|fechas?\s+limites?|horas?\s+limites?)\s+(is\s+|has\s+been\s+|has\s+)?not\s+(agreed|set|required|applicable|requested)\b/i.test(text) ||
    /\b(sin\s+(plazos?|fechas?\s+limites?|horas?\s+limites?)|no\s+(hay|existe|requiere|aplica|se\s+ha\s+acordado|se\s+acuerda).{0,60}\b(plazos?|fechas?\s+limites?|horas?\s+limites?)|(?:plazos?|fechas?\s+limites?|horas?\s+limites?)\b.{0,60}\b(no\s+(acordad[oa]s?|aplicables?|required|requerid[oa]s?|solicitad[oa]s?|fijad[oa]s?|establecid[oa]s?|existe|existen|disponibles?|conocid[oa]s?|indicad[oa]s?)|desconocid[oa]s?))\b/i.test(text)
  ) {
    return false;
  }
  return /\b(agreed[\s_-]*time[\s_-]*limit|time[\s_-]*limits?|delivery[\s_-]*deadlines?|deadlines?|deliver(y)?\s+(before|by)|no\s+later\s+than|not\s+later\s+than|latest\s+(delivery|arrival)|plazos?|fechas?\s+limites?|horas?\s+limites?|entregar\s+antes\s+de|no\s+mas\s+tarde)\b/i.test(
    text
  );
};

const textMatchesInsuranceInstruction = (value: unknown): boolean => {
  const text = normalizeTextForCmrKeywordMatching(
    textFragments(value).join(' ').replace(/\s+/g, ' ').trim()
  );
  if (!/\b(insurance|insured|insure|coverage|policy|seguros?|asegurar|asegurado|coberturas?|polizas?)\b/i.test(text)) {
    return false;
  }
  if (
    /\b(no|not|without|none|waive|waived)\s+(cargo\s+)?(insurance|insured|insure|coverage|policy)\b/i.test(text) ||
    /\b(insurance|coverage)\s+(is\s+)?not\s+(required|requested|needed|applicable)\b/i.test(text) ||
    /\bsin\s+(seguro|cobertura|poliza)\b/i.test(text) ||
    /\bno\s+(asegurar|seguro|cobertura|poliza)\b/i.test(text) ||
    /\bno\s+(se\s+requiere|hay|existe|se\s+solicita).{0,60}\b(seguro|cobertura|poliza)\b/i.test(text) ||
    /\b(seguros?|coberturas?|polizas?)\b.{0,60}\b(?:desconocid[ao]s?|no\s+(?:requerid[oa]s?|solicitad[oa]s?|necesari[oa]s?|aplicables?|contratad[oa]s?|proporcionad[oa]s?|disponibles?|conocid[ao]s?|indicad[ao]s?))\b/i.test(text)
  ) {
    return false;
  }
  return /\b(request(s|ed)?|provide(d)?|obtain(ed)?|arrange(d)?|cover(ed)?|insure(d)?|insurance|coverage|policy|solicita|contrata|asegurar|asegurado|seguros?|coberturas?|polizas?)\b/i.test(
    text
  );
};

const textMatchesDocumentsHandedToCarrier = (...values: unknown[]): boolean => {
  const text = normalizeTextForCmrKeywordMatching(
    values.flatMap(textFragments).join(' ').replace(/\s+/g, ' ').trim()
  );
  const documentNoun = '(documents?|documentos?|documentacion)';
  const handoverVerb =
    '(hand(ed)?|provided|supplied|delivered|given|submitted|presented|transmitted|attached|entregad[oa]s?|proporcionad[oa]s?|aportad[oa]s?|facilitad[oa]s?|presentad[oa]s?)';
  const unavailableDocumentText =
    '(desconocid[oa]s?|no\\s+(disponibles?|conocid[oa]s?|indicad[oa]s?|identificad[oa]s?|declarad[oa]s?|present[ea]s?))';
  if (
    new RegExp(
      `\\b(no|not|without|never)\\s+(${documentNoun})?\\s*${handoverVerb}\\b`,
      'i'
    ).test(text) ||
    new RegExp(
      `\\b${documentNoun}\\s+(were\\s+|are\\s+|have\\s+been\\s+|has\\s+been\\s+)?not\\s+${handoverVerb}\\b`,
      'i'
    ).test(text) ||
    /\b(carrier|transportista|porteador)\s+(has\s+|have\s+|did\s+)?not\s+(received|accepted|recibe|recibio|recibido)\b/i.test(text) ||
    new RegExp(
      `\\b(sin\\s+entregar\\s+(${documentNoun}\\s+)?al\\s+(transportista|porteador)|${documentNoun}\\s+no\\s+${handoverVerb}\\s+al\\s+(transportista|porteador)|(transportista|porteador)\\s+no\\s+(recibe|recibio|ha\\s+recibido)\\s+${documentNoun})\\b`,
      'i'
    ).test(text) ||
    new RegExp(
      `\\b${documentNoun}\\b.{0,80}\\b${unavailableDocumentText}\\b`,
      'i'
    ).test(text)
  ) {
    return false;
  }
  return (
    new RegExp(`\\b${handoverVerb}\\b.{0,120}\\b(carrier|transportista|porteador)\\b`, 'i').test(text) ||
    /\b(carrier|transportista|porteador)\b.{0,120}\b(received|receipt|accepted|recibe|recibido|entregad[oa]s?)\b/i.test(
      text
    )
  );
};

const referencedDocumentHasAttachedFileEvidence = (document: {
  AttachedSpecifiedBinaryFile?: unknown;
}): boolean => {
  const attachedFiles = Array.isArray(document.AttachedSpecifiedBinaryFile)
    ? document.AttachedSpecifiedBinaryFile
    : document.AttachedSpecifiedBinaryFile
    ? [document.AttachedSpecifiedBinaryFile]
    : [];

  return attachedFiles.some((file) => {
    const attachedFile = file as {
      ID?: unknown;
      URIID?: unknown;
      MIMECode?: unknown;
      EncodingCode?: unknown;
      CharacterSetCode?: unknown;
      SizeMeasure?: unknown;
    };
    return (
      hasText(attachedFile.ID) ||
      hasText(attachedFile.URIID) ||
      hasText(attachedFile.MIMECode) ||
      hasText(attachedFile.EncodingCode) ||
      hasText(attachedFile.CharacterSetCode) ||
      measureHasPositiveValue(attachedFile.SizeMeasure)
    );
  });
};

const referencedDocumentHasEvidence = (document: unknown): boolean => {
  if (!document || typeof document !== 'object') {
    return false;
  }

  const candidate = document as {
    Remarks?: unknown;
    Description?: unknown;
    Name?: unknown;
    AttachedSpecifiedBinaryFile?: unknown;
  };
  return (
    referencedDocumentHasAttachedFileEvidence(candidate) ||
    textMatchesDocumentsHandedToCarrier(candidate.Remarks, candidate.Description, candidate.Name)
  );
};

const hasAgreedTimeLimit = (consignment?: SupplyChainConsignment): boolean =>
  Boolean(
    consignment?.DeliveryInstructions?.some(
      (instruction) => textMatchesAgreedTimeLimit(instruction.Description, instruction.DescriptionCode)
    ) ||
      textMatchesAgreedTimeLimit(consignment?.DeliveryTransportEvent?.Description) ||
      consignment?.DeliveryTransportEvent?.SpecifiedTransportInstructions?.some(
        (instruction) => textMatchesAgreedTimeLimit(instruction.Description, instruction.DescriptionCode)
      ) ||
      consignment?.DeliveryTransportEvent?.ApplicableNote?.some(
        (note) =>
          textMatchesAgreedTimeLimit(note.Subject, note.SubjectCode, note.Content, note.ContentCode)
      )
  );

const validators: Record<string, (document: ECMR) => boolean> = {
  'CMR-6.1-a-date-place': (document) =>
    Boolean(
      hasValidDateTimeString(document.ExchangedDocument?.IssueDateTime?.DateTimeString) &&
        hasLocation(document.ExchangedDocument?.IssueLogisticsLocation)
    ),
  'CMR-6.1-b-sender': (document) =>
    hasNamedAddress(document.SpecifiedSupplyChainConsignment?.ConsignorTradeParty),
  'CMR-6.1-c-carrier': (document) =>
    hasNamedAddress(document.SpecifiedSupplyChainConsignment?.CarrierTradeParty),
  'CMR-6.1-d-taking-over-delivery': (document) => {
    const consignment = document.SpecifiedSupplyChainConsignment;

    return (
      hasLocation(consignment?.CarrierAcceptanceLogisticsLocation) &&
      hasEventDate(consignment?.PickUpTransportEvent) &&
      hasLocation(consignment?.ConsigneeReceiptLogisticsLocation) &&
      hasEventDate(consignment?.DeliveryTransportEvent)
    );
  },
  'CMR-6.1-e-consignee': (document) =>
    hasNamedAddress(document.SpecifiedSupplyChainConsignment?.ConsigneeTradeParty),
  'CMR-6.1-f-goods-packing': (document) =>
    hasGoodsAndPacking(document.SpecifiedSupplyChainConsignment),
  'CMR-6.1-g-packages-marks': (document) =>
    hasPackageNumbersAndMarks(document.SpecifiedSupplyChainConsignment),
  'CMR-6.1-h-weight-quantity': (document) =>
    hasWeightOrQuantity(document.SpecifiedSupplyChainConsignment),
  'CMR-6.1-i-charges': (document) =>
    hasCharges(document.SpecifiedSupplyChainConsignment as ExtendedConsignment),
  'CMR-6.1-j-customs-formalities': (document) =>
    hasCustomsInstructions(document.SpecifiedSupplyChainConsignment as ExtendedConsignment),
  'CMR-6.1-k-cmr-statement': hasCmrStatement,
};

const optionalApplicabilityByRequirementId: Record<string, OptionalApplicabilityKey> = {
  'CMR-6.2-a-transshipment-prohibited': 'transshipmentProhibition',
  'CMR-6.2-b-sender-paid-charges': 'senderPaidCharges',
  'CMR-6.2-c-cash-on-delivery': 'cashOnDelivery',
  'CMR-6.2-d-declared-value-special-interest': 'declaredValueOrSpecialInterest',
  'CMR-6.2-e-insurance-instructions': 'insuranceInstructions',
  'CMR-6.2-f-agreed-time-limit': 'agreedTimeLimit',
  'CMR-6.2-g-documents-handed-to-carrier': 'documentsHandedToCarrier',
};

const optionalValidators: Record<string, (document: ECMR) => boolean> = {
  'CMR-6.2-a-transshipment-prohibited': (document) =>
    document.SpecifiedSupplyChainConsignment?.TransshipmentPermissionIndicator === false,
  'CMR-6.2-b-sender-paid-charges': (document) =>
    hasSenderPaidCharges(document.SpecifiedSupplyChainConsignment),
  'CMR-6.2-c-cash-on-delivery': (document) =>
    amountLikeHasPositiveCurrencyValue(document.SpecifiedSupplyChainConsignment?.CODAmount),
  'CMR-6.2-d-declared-value-special-interest': (document) =>
    hasDeclaredValueOrSpecialInterest(document.SpecifiedSupplyChainConsignment),
  'CMR-6.2-e-insurance-instructions': (document) =>
    textMatchesInsuranceInstruction(
      document.SpecifiedSupplyChainConsignment?.CargoInsuranceInstructionsInformation
    ),
  'CMR-6.2-f-agreed-time-limit': (document) =>
    hasAgreedTimeLimit(document.SpecifiedSupplyChainConsignment),
  'CMR-6.2-g-documents-handed-to-carrier': (document) =>
    Boolean(
      document.SpecifiedSupplyChainConsignment?.AssociatedReferencedDocument?.some(
        referencedDocumentHasEvidence
      ) ||
        document.SpecifiedSupplyChainConsignment?.IncludedSupplyChainConsignmentItem?.some((item) =>
          item.AssociatedReferencedDocument?.some(referencedDocumentHasEvidence)
        )
    ),
};

const isOptionalRequirementApplicable = (
  requirement: CmrRequirement,
  document: ECMR
): boolean => {
  const applicabilityKey = optionalApplicabilityByRequirementId[requirement.id];

  return Boolean(
    applicabilityKey &&
      document.SpecifiedSupplyChainConsignment?.CmrOptionalParticularsApplicable?.[applicabilityKey]
  );
};

const buildMissingRequirement = (
  requirement: CmrRequirement,
  message: string
): CmrValidationError => ({
  requirementId: requirement.id,
  severity: requirement.severity,
  message,
  fieldPath: requirement.fieldPaths.join(' | '),
  source: requirement.source,
});

const buildScopeError = (): CmrValidationError => ({
  requirementId: 'CMR-1.1-international-carriage',
  severity: 'error',
  message:
    'CMR scope requires machine-readable taking-over and delivery country codes in different countries, with at least one current CMR contracting country.',
  fieldPath:
    'SpecifiedSupplyChainConsignment.CarrierAcceptanceLogisticsLocation.PostalTradeAddress.CountryID | SpecifiedSupplyChainConsignment.ConsigneeReceiptLogisticsLocation.PostalTradeAddress.CountryID',
  source: {
    document: 'CMR_1956.txt',
    article: '1.1',
    reference: 'Article 1.1, docs/regulations/CMR_1956.txt:35-51',
  },
});

const hasValidCmrArticle2MultimodalDeclaration = (
  document: ECMR
): boolean => {
  const declaration = document.SpecifiedSupplyChainConsignment?.CmrArticle2MultimodalCarriage;
  if (declaration?.vehicleCarriedByOtherMode !== true) {
    return true;
  }

  return Boolean(
    declaration.otherModeType &&
      cmrArticle2OtherModeTypes.has(declaration.otherModeType) &&
      (declaration.goodsUnloadedFromVehicle !== true ||
        declaration.article14ImpedimentBasis === true)
  );
};

const buildCmrArticle2MultimodalError = (): CmrValidationError => ({
  requirementId: 'CMR-2-multimodal-vehicle-carriage',
  severity: 'error',
  message:
    'CMR Article 2 declared vehicle-on-other-mode carriage requires an allowed other mode and no unloading unless Article 14 basis is recorded.',
  fieldPath: 'SpecifiedSupplyChainConsignment.CmrArticle2MultimodalCarriage',
  source: {
    document: 'CMR_1956.txt',
    article: '2',
    reference: 'Article 2, docs/regulations/CMR_1956.txt:55-57',
  },
});

const buildEcmrProtocolPartyScopeError = (): CmrValidationError => ({
  requirementId: 'eCMR-Protocol-Article-2-party-scope',
  severity: 'error',
  message:
    'eCMR Protocol scope requires at least one taking-over or delivery country code to be a current Additional Protocol party.',
  fieldPath:
    'SpecifiedSupplyChainConsignment.CarrierAcceptanceLogisticsLocation.PostalTradeAddress.CountryID | SpecifiedSupplyChainConsignment.ConsigneeReceiptLogisticsLocation.PostalTradeAddress.CountryID',
  source: {
    document: 'CMR_2008.txt',
    article: '2',
    reference: 'Article 2, docs/regulations/CMR_2008.txt:38-40; UNTC XI-B-11-b status 2026-07-09',
  },
});

const buildElectronicSupplementaryDocumentIntegrityLinkError = (): CmrValidationError => ({
  requirementId: 'eCMR-Protocol-Article-6-supplementary-document-integrity-link',
  severity: 'error',
  message:
    'Each electronic supplementary document reference in the eCMR must resolve to a distinct current-version attachment with integrity evidence before issue/export.',
  fieldPath:
    'SpecifiedSupplyChainConsignment.AssociatedReferencedDocument | supplementaryDocuments',
  source: {
    document: 'CMR_2008.txt',
    article: '6.2',
    reference: 'Article 6, docs/regulations/CMR_2008.txt:100-105',
  },
});

export const validateCmrDocument = (document: ECMR): CmrValidationResult => {
  const mandatoryErrors = mandatoryCmrRequirements.flatMap((requirement) => {
    const isSatisfied = validators[requirement.id]?.(document) ?? false;

    if (isSatisfied) {
      return [];
    }

    return [
      buildMissingRequirement(
        requirement,
        `Missing mandatory CMR Article ${requirement.article} particular: ${requirement.label}.`
      ),
    ];
  });

  const conditionalOptionalErrors = selectedOptionalCmrRequirements.flatMap((requirement) => {
    if (!isOptionalRequirementApplicable(requirement, document)) {
      return [];
    }

    const isSatisfied = optionalValidators[requirement.id]?.(document) ?? false;

    if (isSatisfied) {
      return [];
    }

    return [
      buildMissingRequirement(
        requirement,
        `Missing applicable optional CMR Article ${requirement.article} particular: ${requirement.label}.`
      ),
    ];
  });

  const scopeErrors = hasInternationalCarriageScope(document.SpecifiedSupplyChainConsignment)
    ? []
    : [buildScopeError()];

  const cmrArticle2Errors = hasValidCmrArticle2MultimodalDeclaration(document)
    ? []
    : [buildCmrArticle2MultimodalError()];

  const protocolErrors = [
    ...(hasEcmrProtocolPartyScope(document.SpecifiedSupplyChainConsignment)
      ? []
      : [buildEcmrProtocolPartyScopeError()]),
    ...(electronicSupplementaryDocumentReferences(document.SpecifiedSupplyChainConsignment).length > 0 &&
    !hasLinkedSupplementaryDocumentEvidence(document)
      ? [buildElectronicSupplementaryDocumentIntegrityLinkError()]
      : []),
  ];

  const errors = [
    ...mandatoryErrors,
    ...scopeErrors,
    ...cmrArticle2Errors,
    ...conditionalOptionalErrors,
    ...protocolErrors,
  ];

  return {
    valid: !errors.some((error) => error.severity === 'error'),
    errors,
  };
};
